import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ApiError } from '@/lib/api/errors';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (err: Error, retry: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  err: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { err: null };

  static getDerivedStateFromError(err: Error): ErrorBoundaryState {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[error-boundary]', err, info.componentStack);
  }

  private retry = () => this.setState({ err: null });

  render() {
    const { err } = this.state;
    if (!err) return this.props.children;

    if (this.props.fallback) return this.props.fallback(err, this.retry);

    const status = err instanceof ApiError ? err.status : null;
    const traceId = err instanceof ApiError ? err.traceId : null;

    return (
      <div className="page">
        <div className="page__inner">
          <main className="page__main" role="alert">
            <p className="note note--error">{err.message || 'Something went wrong.'}</p>
            {status !== null && (
              <p className="note note--soft">HTTP {status}{traceId ? ` · ${traceId}` : ''}</p>
            )}
            <p>
              <button type="button" className="action" onClick={this.retry}>retry</button>
            </p>
          </main>
        </div>
      </div>
    );
  }
}
